// src/AppRoutes.tsx
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { lazy, Suspense, ReactNode } from "react";
// Eager imports moved to the top to satisfy import/first rule
import SubscriptionsPage from "./modules/admin/pages/AdminSubscriptionsPage";
import SubscriptionBillingPage from "./modules/admin/pages/SubscriptionBillingPage";
import Home from "./pages/Home";
import LoginPage from "./pages/LoginPage";
import OnboardingPage from "./pages/OnboardingPage";
import VerifyPage from "./pages/VerifyPage";
import LandingPage from "./pages/LandingPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ClientsListPage from "./modules/clients/pages/ClientsListPage";
import AddClientPage from "./modules/clients/pages/AddClientPage";
import ClientProfilePage from "./modules/clients/pages/ClientProfilePage";
import EditClientPage from "./modules/clients/pages/EditClientPage";
import TicketsListPage from "./modules/tickets/pages/TicketsListPage";
import CreateTicketPage from "./modules/tickets/pages/CreateTicketPage";
import EditTicketPage from "./modules/tickets/pages/EditTicketPage";
import TicketDetailPage from "./modules/tickets/pages/TicketDetailPage";
import TicketPrintView from "./modules/tickets/pages/TicketPrintView";
import TicketMiniLabelView from "./modules/tickets/pages/TicketMiniLabelView";
import ManageCategoriesPage from "./modules/categories/pages/ManageCategoriesPage";
import InvoicesListPage from "./modules/invoices/pages/InvoicesListPage";
import InvoiceDetailPage from "./modules/invoices/pages/InvoiceDetailPage";
import CreateInvoicePage from "./modules/invoices/pages/CreateInvoicePage";
import InvoicePrintView from "./modules/invoices/pages/InvoicePrintView";
import AdminLayout from "./layouts/AdminLayout";
import { useAuth } from "./context/AuthContext";
import EditInvoicePage from './modules/invoices/pages/EditInvoicePage';
import POSDashboard from "./modules/pos/pages/POSDashboard";
import PosTerminalPage from "./modules/pos/pages/PosTerminalPage";
import SaleDetailPage from "./modules/pos/pages/SaleDetailPage";
// const PosReportsPage declared after imports


import InventoryListPage from "./modules/inventory/pages/InventoryListPage"; // Optional
import InventoryDetailPage from "./modules/inventory/pages/InventoryDetailPage"; // Optional
import InventoryEditPage from "./modules/inventory/pages/InventoryEditPage"; // Optional
import InventoryAddPage from "./modules/inventory/pages/InventoryAddPage"; // Optional
import MovementLogPage from "./modules/inventory/pages/MovementLogPage"; // Optional
import InventoryAdjustPage from "./modules/inventory/pages/InventoryAdjustPage"; // Optional
import SupplierListPage from "./modules/inventory/pages/SuppliersListPage"; // Optional
import AddSupplierPage from "./modules/inventory/pages/AddSupplierPage"; // Optional
import EditSupplierPage from "./modules/inventory/pages/EditSupplierPage"; // Optional
import PurchaseOrdersListPage from "./modules/inventory/pages/PurchaseOrdersListPage"; // Optional
import PurchaseOrderDetailPage from "./modules/inventory/pages/PurchaseOrderDetailPage"; // Optional
import CreatePurchaseOrderPage from "./modules/inventory/pages/CreatePurchaseOrderPage"; // Optional
import AccountsDashboardPage from "./modules/accounts/pages/DashboardPage"; // 👈 Adjust the path if needed
import JournalsPage from "./modules/accounts/pages/JournalsPage";
import CreateJournalPage from "./modules/accounts/pages/CreateJournalPage"; // 👈 Adjust the path if needed
import PurchasesPage from "./modules/accounts/pages/PurchasesPage";
import ProfitLossPage from "./modules/accounts/pages/ProfitLossPage";
import TrialBalancePage from "./modules/accounts/pages/TrialBalancePage";
import BalanceSheetPage from "./modules/accounts/pages/BalanceSheetPage";
import AccountingSettingsPage from "./modules/accounts/pages/SettingsPage";
import AccountingMigrationsPage from "./modules/accounts/pages/MigrationsPage";
import EmployeeListPage from "./modules/hr/pages/EmployeesListPage";
import AddEmployeePage from "./modules/hr/pages/AddEmployeePage";
import ViewEmployeePage from "./modules/hr/pages/ViewEmployeePage";
import ContractListPage from "./modules/hr/pages/ContractsListPage";
import UsersPage from "./modules/settings/pages/UsersPage";
import GeneralSettingsPage from "./modules/settings/pages/GeneralSettingsPage";
import ChooseIndustryPage from "./pages/ChooseIndustryPage";
import BrandingTemplatesPage from "./modules/settings/pages/BrandingTemplatesPage";
import NotificationsSettingsPage from "./modules/settings/pages/NotificationsSettingsPage";
import ManageSubscriptionPage from "./modules/settings/pages/ManageSubscriptionPage";
import PlansPage from "./modules/admin/pages/PlansPage";
import CapitalInjectionPage from "./modules/accounts/pages/CapitalInjectionPage";
import LoanPage from "./modules/accounts/pages/LoanPage";
import ActivityListPage from "./pages/ActivityListPage";





const PosReportsPage = lazy(() => import("./modules/pos/pages/PosReportsPage"));
const TenantsPage = lazy(() => import("./modules/admin/pages/TenantsAdminPage"));

type PrivateRouteProps = {
  children: ReactNode;
};

function PrivateRoute({ children }: PrivateRouteProps) {
  const { token } = useAuth();
  return token ? <>{children}</> : <Navigate to="/landing" replace />;
}
        

export default function AppRoutes() {
  return (
    <Router>
      <Routes>
        {/* Public */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<OnboardingPage />} />
        <Route path="/landing" element={<LandingPage />} />
        <Route path="/verify" element={<VerifyPage />} />

        {/* Protected with AdminLayout */}
        <Route path="/" element={<PrivateRoute><AdminLayout><Home /></AdminLayout></PrivateRoute>} />
        <Route path="/clients" element={<PrivateRoute><AdminLayout><ClientsListPage /></AdminLayout></PrivateRoute>} />
        <Route path="/clients/add" element={<PrivateRoute><AdminLayout><AddClientPage /></AdminLayout></PrivateRoute>} />
        <Route path="/clients/:clientId/profile" element={<PrivateRoute><AdminLayout><ClientProfilePage /></AdminLayout></PrivateRoute>} />
        <Route path="/clients/:clientId/edit" element={<PrivateRoute><AdminLayout><EditClientPage /></AdminLayout></PrivateRoute>} />

        <Route path="/tickets" element={<PrivateRoute><AdminLayout><TicketsListPage /></AdminLayout></PrivateRoute>} />
        <Route path="/tickets/create" element={<PrivateRoute><AdminLayout><CreateTicketPage /></AdminLayout></PrivateRoute>} />
        <Route path="/tickets/:id/edit" element={<PrivateRoute><AdminLayout><EditTicketPage /></AdminLayout></PrivateRoute>} />
        <Route path="/tickets/:id" element={<PrivateRoute><AdminLayout><TicketDetailPage /></AdminLayout></PrivateRoute>} />
        <Route path="/manage-categories" element={<PrivateRoute><AdminLayout><ManageCategoriesPage /></AdminLayout></PrivateRoute>} />
        <Route path="/invoices" element={<PrivateRoute><AdminLayout><InvoicesListPage /></AdminLayout></PrivateRoute>} />
        <Route path="/invoices/:id" element={<PrivateRoute><AdminLayout><InvoiceDetailPage /></AdminLayout></PrivateRoute>} />
        <Route path="/invoices/create" element={<PrivateRoute><AdminLayout><CreateInvoicePage /></AdminLayout></PrivateRoute>} />
        <Route path="/invoices/:id/edit" element={<PrivateRoute><AdminLayout><EditInvoicePage /></AdminLayout></PrivateRoute> }/>
        <Route path="/pos" element={<PrivateRoute><AdminLayout><POSDashboard /></AdminLayout></PrivateRoute>} />
        <Route path="/pos/new" element={<PrivateRoute><AdminLayout><PosTerminalPage /></AdminLayout></PrivateRoute>} />
        <Route path="/pos/sale/:id" element={<PrivateRoute><AdminLayout><SaleDetailPage /></AdminLayout></PrivateRoute>} />
        <Route path="/pos/reports" element={<PrivateRoute><AdminLayout><Suspense fallback={<div>Loading…</div>}><PosReportsPage /></Suspense></AdminLayout></PrivateRoute>} />

        {/*  Inventory Management */}
        <Route path="/inventory" element={<PrivateRoute><AdminLayout><InventoryListPage /></AdminLayout></PrivateRoute>} />
        <Route path="/inventory/add" element={<PrivateRoute><AdminLayout><InventoryAddPage /></AdminLayout></PrivateRoute>} />
        <Route path="/inventory/:id/edit" element={<PrivateRoute><AdminLayout><InventoryEditPage /></AdminLayout></PrivateRoute>} />
        <Route path="/inventory/:id" element={<PrivateRoute><AdminLayout><InventoryDetailPage /></AdminLayout></PrivateRoute>} />
        <Route path="/inventory/:id/movement-log" element={<PrivateRoute><AdminLayout><MovementLogPage /></AdminLayout></PrivateRoute>} />
        <Route path="/inventory/movement-log" element={<PrivateRoute><AdminLayout><MovementLogPage /></AdminLayout></PrivateRoute>} />
        <Route path="/inventory/:id/adjust" element={<PrivateRoute><AdminLayout><InventoryAdjustPage /></AdminLayout></PrivateRoute>} />
        <Route path="/suppliers" element={<PrivateRoute><AdminLayout><SupplierListPage /></AdminLayout></PrivateRoute>} />
        <Route path="/suppliers/add" element={<PrivateRoute><AdminLayout><AddSupplierPage /></AdminLayout></PrivateRoute>} />
        <Route path="/suppliers/:id/edit" element={<PrivateRoute><AdminLayout><EditSupplierPage /></AdminLayout></PrivateRoute>} />
        <Route path="/purchase-orders" element={<PrivateRoute><AdminLayout><PurchaseOrdersListPage /></AdminLayout></PrivateRoute>} />
        <Route path="/purchase-orders/:id" element={<PrivateRoute><AdminLayout><PurchaseOrderDetailPage /></AdminLayout></PrivateRoute>} />
        <Route path="/purchase-orders/create" element={<PrivateRoute><AdminLayout><CreatePurchaseOrderPage /></AdminLayout></PrivateRoute>} />
        

         {/*  Finance Management */}
        <Route path="/accounts" element={<PrivateRoute><AdminLayout><AccountsDashboardPage /></AdminLayout></PrivateRoute>} />
        <Route path="/accounts/journals" element={<PrivateRoute><AdminLayout><JournalsPage /></AdminLayout></PrivateRoute>} />
        <Route path="/accounts/journals/create" element={<PrivateRoute><AdminLayout><CreateJournalPage /></AdminLayout></PrivateRoute>} />
        <Route path="/accounts/finance/capital" element={<PrivateRoute><AdminLayout><CapitalInjectionPage /></AdminLayout></PrivateRoute>} />
        <Route path="/accounts/finance/loan" element={<PrivateRoute><AdminLayout><LoanPage /></AdminLayout></PrivateRoute>} />
        <Route path="/accounts/purchases" element={<PrivateRoute><AdminLayout><PurchasesPage /></AdminLayout></PrivateRoute>} />
        <Route path="/accounts/profit-loss" element={<PrivateRoute><AdminLayout><ProfitLossPage /></AdminLayout></PrivateRoute>} />
        <Route path="/accounts/trial-balance" element={<PrivateRoute><AdminLayout><TrialBalancePage /></AdminLayout></PrivateRoute>} />
        <Route path="/accounts/balance-sheet" element={<PrivateRoute><AdminLayout><BalanceSheetPage /></AdminLayout></PrivateRoute>} />
        <Route path="/accounts/settings" element={<PrivateRoute><AdminLayout><AccountingSettingsPage /></AdminLayout></PrivateRoute>} />
        <Route path="/accounts/tools" element={<PrivateRoute><AdminLayout><AccountingMigrationsPage /></AdminLayout></PrivateRoute>} />
        <Route path="/activity" element={<PrivateRoute><AdminLayout><ActivityListPage /></AdminLayout></PrivateRoute>} />

        {/* HR Management */}
        <Route path="/hr/employees" element={<PrivateRoute><AdminLayout><EmployeeListPage /></AdminLayout></PrivateRoute>} />
        <Route path="/hr/employees/add" element={<PrivateRoute><AdminLayout><AddEmployeePage /></AdminLayout></PrivateRoute>} />
        <Route path="/hr/employees/:id" element={<PrivateRoute><AdminLayout><ViewEmployeePage /></AdminLayout></PrivateRoute>} />
        <Route path="/hr/contracts" element={<PrivateRoute><AdminLayout><ContractListPage /></AdminLayout></PrivateRoute>} />
        
        <Route path="/settings" element={<PrivateRoute><AdminLayout><GeneralSettingsPage /></AdminLayout></PrivateRoute>} />
        <Route path="/settings/general" element={<PrivateRoute><AdminLayout><GeneralSettingsPage /></AdminLayout></PrivateRoute>} />
        <Route path="/choose-industry" element={<PrivateRoute><ChooseIndustryPage /></PrivateRoute>} />
        <Route path="/settings/branding" element={<PrivateRoute><AdminLayout><BrandingTemplatesPage /></AdminLayout></PrivateRoute>} />
        <Route path="/settings/notifications" element={<PrivateRoute><AdminLayout><NotificationsSettingsPage /></AdminLayout></PrivateRoute>} />
        <Route path="/settings/subscription" element={<PrivateRoute><AdminLayout><ManageSubscriptionPage /></AdminLayout></PrivateRoute>} />
        <Route path="/settings/users" element={<PrivateRoute><AdminLayout><UsersPage /></AdminLayout></PrivateRoute>} />
        {/* SaaS Admin */}
        <Route path="/control" element={<PrivateRoute><AdminLayout><TenantsPage /></AdminLayout></PrivateRoute>} />
        <Route path="/control/plans" element={<PrivateRoute><AdminLayout><PlansPage /></AdminLayout></PrivateRoute>} />
        <Route path="/control/subscriptions" element={<PrivateRoute><AdminLayout><SubscriptionsPage /></AdminLayout></PrivateRoute>} />
        <Route path="/control/billing" element={<PrivateRoute><AdminLayout><SubscriptionBillingPage /></AdminLayout></PrivateRoute>} />



        {/* Public auth helpers */}
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Admin Layout */}


        {/* Redirect to home if no match */}
      


        {/* ✅ Full Ticket Print View = NO AdminLayout */}
         <Route path="/tickets/:id/print" element={
       <PrivateRoute><TicketPrintView /></PrivateRoute>
        } />

      {/* ✅ Mini Label Print View = NO AdminLayout */}
        <Route path="/tickets/:id/label" element={
       <PrivateRoute><TicketMiniLabelView /></PrivateRoute>
      } />

      <Route path="/invoices/:id/print" element={
        <PrivateRoute><InvoicePrintView /></PrivateRoute>} />

      </Routes>
    </Router>
  );
}









